/**
 * Workshop version of ToolFrame.
 *
 * Props-based, no React Flow or store dependencies.
 */

import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";

function statusColor(status: string): string {
  if (status === "streaming") return "bg-status-streaming";
  if (status === "error") return "bg-status-error";
  return "bg-muted";
}

interface ToolFrameProps {
  title: string;
  toolName: string;
  input: string;
  output: string;
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  onCopy?: () => void;
  onResizeStart?: React.MouseEventHandler;
}

export function ToolFrame({
  title,
  toolName,
  input,
  output,
  status,
  agentName,
  onCopy,
  onResizeStart,
}: ToolFrameProps) {
  return (
    <div
      className="denker-glass-frame flex flex-col rounded-frame overflow-hidden"
      data-testid="tool-frame"
    >
      <FrameHeader
        title={title || toolName}
        icon={Icons.Wrench}
        accentColor="bg-frame-tool"
        agentName={agentName}
        statusColor={statusColor(status)}
        data-testid="tool-frame-header"
      />

      <div className="scrollbar-none flex-1 overflow-auto px-3 py-2">
        <div className="mb-1.5">
          <span className="rounded bg-elevated/40 px-1.5 py-0.5 text-canvas-base font-medium text-muted">
            {toolName}
          </span>
        </div>

        {input && (
          <div className="mb-2">
            <p className="text-canvas-base font-medium text-muted mb-1">Input</p>
            <pre className="scrollbar-none bg-elevated/30 rounded-md p-2 text-canvas-base text-secondary overflow-x-auto leading-relaxed">
              <code>{input}</code>
            </pre>
          </div>
        )}

        {status === "streaming" && !output ? (
          <div className="flex items-center gap-2 py-3">
            <Spinner className="h-3 w-3" />
            <span className="text-canvas-base text-muted">Executing...</span>
          </div>
        ) : output ? (
          <div>
            <p className="text-canvas-base font-medium text-muted mb-1">Output</p>
            <pre className="scrollbar-none bg-elevated/30 rounded-md p-2 text-canvas-base text-secondary overflow-x-auto leading-relaxed">
              <code>{output}</code>
            </pre>
          </div>
        ) : (
          <p className="py-2 text-center text-canvas-base italic text-muted">
            No output yet
          </p>
        )}
      </div>

      <FrameFooter onCopy={onCopy} onResizeStart={onResizeStart} data-testid="tool-frame-footer" />
    </div>
  );
}
